import gql from 'graphql-tag';

// -----------------------------------------------------------------------------
// Shared / Re-usable sub-fragments
// -----------------------------------------------------------------------------

export const DynamicFilterByComponent = gql`
  fragment DynamicFilterByComponent on DynamicFilterByComponentObject {
    componentId
    value {
      stringWildcard {
        value
      }
      containsNumber {
        values
        negate
      }
      containsString {
        values
        negate
      }
      dateRange {
        before
        after
      }
      dateIs {
        operator
        relativeDate {
          duration
          timeUnit
        }
      }
      dateTokenIs {
        value
      }
      numberIs {
        operator
        value
      }
      isEmpty {
        value
      }
      containsCurrentUser {
        value
      }
      containsCurrentWorkflowTemplateComponentValue {
        componentId
      }
    }
  }
`;

// -----------------------------------------------------------------------------
// Individual component fragments
// -----------------------------------------------------------------------------

export const AddressComponent = gql`
  fragment AddressComponent on AddressComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    isServerManaged
    managedByComponentId
  }
`;

export const AddressComponentOverride = gql`
  fragment AddressComponentOverride on AddressComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    isHidden
  }
`;

export const AddressComponentDataValue = gql`
  fragment AddressComponentDataValue on AddressComponentDataValue {
    coords {
      lat
      lon
    }
    googlePlaceId
    subtitle
    title
  }
`;

export const AddressComponentDataChange = gql`
  fragment AddressComponentDataChange on AddressComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      addressValue: value {
        ...AddressComponentDataValue
      }
    }
    originalAuditData {
      type
      addressValue: value {
        ...AddressComponentDataValue
      }
    }
  }

  ${AddressComponentDataValue}
`;

export const AuditCheckboxComponent = gql`
  fragment AuditCheckboxComponent on AuditCheckboxComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    isServerManaged
    managedByComponentId
  }
`;

export const AuditCheckboxComponentOverride = gql`
  fragment AuditCheckboxComponentOverride on AuditCheckboxComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    isHidden
  }
`;

export const AuditCheckboxComponentDataValue = gql`
  fragment AuditCheckboxComponentDataValue on AuditCheckboxComponentDataValue {
    files {
      name
      url
    }
    notes
    checked
  }
`;

export const AuditCheckboxComponentDataChange = gql`
  fragment AuditCheckboxComponentDataChange on AuditCheckboxComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      auditCheckboxValue: value {
        ...AuditCheckboxComponentDataValue
      }
    }
    originalAuditData {
      type
      auditCheckboxValue: value {
        ...AuditCheckboxComponentDataValue
      }
    }
  }

  ${AuditCheckboxComponentDataValue}
`;

export const AuditTagComponent = gql`
  fragment AuditTagComponent on AuditTagComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    options {
      label
      value
      backgroundColor
      archived
    }
    multiselect
    addAdditionalValues
    default {
      value
    }
    upcomingRecurringEntityDefault {
      value
    }
    firstRecurringEntityDefault {
      value
    }
    isServerManaged
    managedByComponentId
  }
`;

export const AuditTagComponentOverride = gql`
  fragment AuditTagComponentOverride on AuditTagComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    addAdditionalValues
    isHidden
    default {
      value
    }
  }
`;

export const AuditTagComponentDataValue = gql`
  fragment AuditTagComponentDataValue on AuditTagComponentDataValue {
    files {
      name
      url
    }
    notes
    tags
  }
`;

export const AuditTagComponentDataChange = gql`
  fragment AuditTagComponentDataChange on AuditTagComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      auditTagValue: value {
        ...AuditTagComponentDataValue
      }
    }
    originalAuditData {
      type
      auditTagValue: value {
        ...AuditTagComponentDataValue
      }
    }
  }

  ${AuditTagComponentDataValue}
`;

export const AuditTextComponent = gql`
  fragment AuditTextComponent on AuditTextComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    maxlength
    multiline
    isHidden
    isFilterable
    isSortable
    isServerManaged
    managedByComponentId
  }
`;

export const AuditTextComponentOverride = gql`
  fragment AuditTextComponentOverride on AuditTextComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    isHidden
  }
`;

export const AuditTextComponentDataValue = gql`
  fragment AuditTextComponentDataValue on AuditTextComponentDataValue {
    files {
      name
      url
    }
    notes
    text
  }
`;

export const AuditTextComponentDataChange = gql`
  fragment AuditTextComponentDataChange on AuditTextComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      auditTextValue: value {
        ...AuditTextComponentDataValue
      }
    }
    originalAuditData {
      type
      auditTextValue: value {
        ...AuditTextComponentDataValue
      }
    }
  }

  ${AuditTextComponentDataValue}
`;

export const CombinedTagsComponent = gql`
  fragment CombinedTagsComponent on CombinedTagsComponent {
    id
    type
    label
    icon
    locked
    componentIds
    isHidden
    isServerManaged
    managedByComponentId
  }
`;

export const CombinedTagsComponentOverride = gql`
  fragment CombinedTagsComponentOverride on CombinedTagsComponentOverride {
    type
    icon
    label
    isHidden
  }
`;

export const DateComponent = gql`
  fragment DateComponent on DateComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    isServerManaged
    allowEntityBatchCreation
    managedByComponentId
    autoExtendDefault {
      impliedFilterComponentId
      minimumInSeries
    }
    default {
      offsetMs
    }
  }
`;

export const DateComponentOverride = gql`
  fragment DateComponentOverride on DateComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    isHidden
    default {
      offsetMs
    }
  }
`;

export const DateComponentDataChange = gql`
  fragment DateComponentDataChange on DateComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      dateValue: value
    }
    originalAuditData {
      type
      dateValue: value
    }
  }
`;

export const DateRangeComponent = gql`
  fragment DateRangeComponent on DateRangeComponent {
    id
    type
    locked
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    isServerManaged
    startDateComponentId
    endDateComponentId
    managedByComponentId
  }
`;

export const DateRangeComponentOverride = gql`
  fragment DateRangeComponentOverride on DateRangeComponentOverride {
    type
    label
    icon
    isHidden
  }
`;

export const EmailComponent = gql`
  fragment EmailComponent on EmailComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    isServerManaged
    managedByComponentId
    default {
      value
    }
  }
`;

export const EmailComponentOverride = gql`
  fragment EmailComponentOverride on EmailComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    isHidden
    default {
      value
    }
  }
`;

export const EmailComponentDataChange = gql`
  fragment EmailComponentDataChange on EmailComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      emailValue: value
    }
    originalAuditData {
      type
      emailValue: value
    }
  }
`;

export const EntityBatchComponent = gql`
  fragment EntityBatchComponent on EntityBatchComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    isServerManaged
    managedByComponentId
  }
`;

export const EntityBatchComponentOverride = gql`
  fragment EntityBatchComponentOverride on EntityBatchComponentOverride {
    type
    required
    readonly
    label
    icon
    isHidden
  }
`;

export const FileComponent = gql`
  fragment FileComponent on FileComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    acceptedContentTypes
    isHidden
    isFilterable
    isSortable
    isServerManaged
    managedByComponentId
  }
`;

export const FileComponentOverride = gql`
  fragment FileComponentOverride on FileComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    acceptedContentTypes
    isHidden
  }
`;

export const FileComponentDataChange = gql`
  fragment FileComponentDataChange on FileComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      fileValue: value {
        contentType
        height
        name
        url
        width
      }
    }
    originalAuditData {
      type
      fileValue: value {
        contentType
        height
        name
        url
        width
      }
    }
  }
`;

export const GeolocationComponent = gql`
  fragment GeolocationComponent on GeolocationComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    isServerManaged
    managedByComponentId
  }
`;

export const GeolocationComponentOverride = gql`
  fragment GeolocationComponentOverride on GeolocationComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    isHidden
  }
`;

export const GeolocationComponentDataValue = gql`
  fragment GeolocationComponentDataValue on GeolocationComponentDataValue {
    accuracy
    coords {
      lat
      lon
    }
    date
    userId
  }
`;

export const GeolocationComponentDataChange = gql`
  fragment GeolocationComponentDataChange on GeolocationComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      geolocationValue: value {
        ...GeolocationComponentDataValue
      }
    }
    originalAuditData {
      type
      geolocationValue: value {
        ...GeolocationComponentDataValue
      }
    }
  }

  ${GeolocationComponentDataValue}
`;

export const NumberComponent = gql`
  fragment NumberComponent on NumberComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    format
    isHidden
    isFilterable
    isSortable
    isServerManaged
    managedByComponentId
  }
`;

export const NumberComponentOverride = gql`
  fragment NumberComponentOverride on NumberComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    format
    isHidden
  }
`;

export const NumberComponentDataChange = gql`
  fragment NumberComponentDataChange on NumberComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      numberValue: value
    }
    originalAuditData {
      type
      numberValue: value
    }
  }
`;

export const PersonComponent = gql`
  fragment PersonComponent on PersonComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    default {
      token
      userIds
    }
    isServerManaged
    managedByComponentId
  }
`;

export const PersonComponentOverride = gql`
  fragment PersonComponentOverride on PersonComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    default {
      token
      userIds
    }
    isHidden
  }
`;

export const PersonComponentDataChange = gql`
  fragment PersonComponentDataChange on PersonComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      personValue: value
    }
    originalAuditData {
      type
      personValue: value
    }
  }
`;

export const RelatedCardComponent = gql`
  fragment RelatedCardComponent on RelatedCardComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    hasQuantity
    limit
    workflowTemplateId
    listDefault {
      sortByComponents {
        componentId
        direction
      }
      filterByComponents {
        ...DynamicFilterByComponent
      }
    }
    default {
      cardIds
    }
    isServerManaged
    managedByComponentId
  }
  ${DynamicFilterByComponent}
`;

export const RelatedCardComponentOverride = gql`
  fragment RelatedCardComponentOverride on RelatedCardComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    default {
      cardIds
    }
    isHidden
  }
`;

export const RelatedCardComponentDataValue = gql`
  fragment RelatedCardComponentDataValue on RelatedCardComponentDataValue {
    id
    quantity
  }
`;

export const RelatedCardComponentDataChange = gql`
  fragment RelatedCardComponentDataChange on RelatedCardComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      relatedCardValue: value {
        ...RelatedCardComponentDataValue
      }
    }
    originalAuditData {
      type
      relatedCardValue: value {
        ...RelatedCardComponentDataValue
      }
    }
  }

  ${RelatedCardComponentDataValue}
`;

export const RelatedCardLookupComponent = gql`
  fragment RelatedCardLookupComponent on RelatedCardLookupComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    relatedCardComponentId
    lookupWorkflowTemplateId
    lookupComponentId
    secondaryLookupComponentId
    isServerManaged
    managedByComponentId
  }
`;

export const RelatedCardLookupComponentOverride = gql`
  fragment RelatedCardLookupComponentOverride on RelatedCardLookupComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    isHidden
  }
`;

export const ScheduledAutomationComponent = gql`
  fragment ScheduledAutomationComponent on ScheduledAutomationComponent {
    automationId
    dateComponentId
    icon
    id
    isHidden
    isFilterable
    isServerManaged
    isSortable
    label
    locked
    placeholder
    readonly
    required
    type
    default {
      automationTriggerDurations
    }
    managedByComponentId
  }
`;

export const ScheduledAutomationComponentOverride = gql`
  fragment ScheduledAutomationComponentOverride on ScheduledAutomationComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    isHidden
    default {
      automationTriggerDurations
    }
  }
`;

export const ScheduledAutomationComponentDataValue = gql`
  fragment ScheduledAutomationComponentDataValue on ScheduledAutomationComponentDataValue {
    automationTriggerDurations
  }
`;

export const ScheduledAutomationComponentDataChange = gql`
  fragment ScheduledAutomationComponentDataChange on ScheduledAutomationComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      scheduledAutomationValue: value {
        ...ScheduledAutomationComponentDataValue
      }
    }
    originalAuditData {
      type
      scheduledAutomationValue: value {
        ...ScheduledAutomationComponentDataValue
      }
    }
  }

  ${ScheduledAutomationComponentDataValue}
`;

export const SignatureComponent = gql`
  fragment SignatureComponent on SignatureComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    isServerManaged
    managedByComponentId
  }
`;

export const SignatureComponentOverride = gql`
  fragment SignatureComponentOverride on SignatureComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    isHidden
  }
`;

export const SignatureComponentDataValue = gql`
  fragment SignatureComponentDataValue on SignatureComponentDataValue {
    contentType
    height
    name
    url
    width
    date
    userId
  }
`;

export const SignatureComponentDataChange = gql`
  fragment SignatureComponentDataChange on SignatureComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      signatureValue: value {
        ...SignatureComponentDataValue
      }
    }
    originalAuditData {
      type
      signatureValue: value {
        ...SignatureComponentDataValue
      }
    }
  }

  ${SignatureComponentDataValue}
`;

export const StaticTextComponent = gql`
  fragment StaticTextComponent on StaticTextComponent {
    id
    type
    locked
    text
    isHidden
    isServerManaged
    managedByComponentId
  }
`;

export const StaticTextComponentOverride = gql`
  fragment StaticTextComponentOverride on StaticTextComponentOverride {
    type
    isHidden
  }
`;

export const SubformComponent = gql`
  fragment SubformComponent on SubformComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    workflowTemplateIds
    isHidden
    isFilterable
    isSortable
    default {
      workflowTemplateId
    }
    isServerManaged
    managedByComponentId
  }
`;

export const SubformComponentOverride = gql`
  fragment SubformComponentOverride on SubformComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    default {
      workflowTemplateId
    }
    isHidden
  }
`;

export const SubformComponentDataValue = gql`
  fragment SubformComponentDataValue on SubformComponentDataValue {
    workflowTemplateId
  }
`;

export const SubformComponentAuditDataValue = gql`
  fragment SubformComponentAuditDataValue on SubformComponentAuditDataValue {
    workflowTemplateId
  }
`;

export const TagComponent = gql`
  fragment TagComponent on TagComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    options {
      label
      value
      backgroundColor
      archived
    }
    checklistEnabled
    checklistOptions {
      checkedOption
      uncheckedOption
    }
    multiselect
    addAdditionalValues
    default {
      value
    }
    upcomingRecurringEntityDefault {
      value
    }
    firstRecurringEntityDefault {
      value
    }
    isServerManaged
    managedByComponentId
  }
`;

export const TagComponentOverride = gql`
  fragment TagComponentOverride on TagComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    addAdditionalValues
    default {
      value
    }
    isHidden
  }
`;

export const TagComponentDataChange = gql`
  fragment TagComponentDataChange on TagComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      tagValue: value
    }
    originalAuditData {
      type
      tagValue: value
    }
  }
`;

export const TextComponent = gql`
  fragment TextComponent on TextComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    maxlength
    multiline
    isHidden
    isFilterable
    isSortable
    default {
      text
    }
    isServerManaged
    managedByComponentId
  }
`;

export const TextComponentOverride = gql`
  fragment TextComponentOverride on TextComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    multiline
    default {
      text
    }
    isHidden
  }
`;

export const TextComponentDataChange = gql`
  fragment TextComponentDataChange on TextComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      textValue: value
    }
    originalAuditData {
      type
      textValue: value
    }
  }
`;

export const TodoComponent = gql`
  fragment TodoComponent on TodoComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    isServerManaged
    managedByComponentId
  }
`;

export const TodoComponentOverride = gql`
  fragment TodoComponentOverride on TodoComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    isHidden
  }
`;

export const TodoComponentDataChange = gql`
  fragment TodoComponentDataChange on TodoComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      todoComponentAuditDataValue {
        id
        status
        summary
      }
    }
    originalAuditData {
      type
      todoComponentAuditDataValue {
        id
        status
        summary
      }
    }
  }
`;

export const InputButtonComponent = gql`
  fragment InputButtonComponent on InputButtonComponent {
    id
    type
    locked
    isServerManaged
    isHidden
    buttonText
    inputComponentId
    managedByComponentId
  }
`;

export const InputButtonComponentOverride = gql`
  fragment InputButtonComponentOverride on InputButtonComponentOverride {
    type
    isHidden
    buttonText
  }
`;

export const TimeTrackerComponent = gql`
  fragment TimeTrackerComponent on TimeTrackerComponent {
    id
    type
    locked
    required
    isServerManaged
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    startButtonLabel
    endButtonLabel
    requiresGeolocation
    autoStart
    managedByComponentId
  }
`;

export const TimeTrackerComponentOverride = gql`
  fragment TimeTrackerComponentOverride on TimeTrackerComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    autoStart
    isHidden
  }
`;

export const TimeTrackerComponentDataValue = gql`
  fragment TimeTrackerComponentDataValue on TimeTrackerComponentDataValue {
    start {
      geolocation {
        accuracy
        coords {
          lat
          lon
        }
        userId
      }
      timestamp
    }
    end {
      geolocation {
        accuracy
        coords {
          lat
          lon
        }
        userId
      }
      timestamp
    }
  }
`;

export const TimeTrackerComponentDataChange = gql`
  fragment TimeTrackerComponentDataChange on TimeTrackerComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      timeTrackerValue: value {
        ...TimeTrackerComponentDataValue
      }
    }
    originalAuditData {
      type
      timeTrackerValue: value {
        ...TimeTrackerComponentDataValue
      }
    }
  }

  ${TimeTrackerComponentDataValue}
`;

export const ReferencedInComponent = gql`
  fragment ReferencedInComponent on ReferencedInComponent {
    id
    type
    archivedAt
    locked
    label
    isHidden
    isServerManaged
    referencedFromWorkflowTemplateId
    relatedCardComponentId
    referencingViewTemplateId
    managedByComponentId
  }
`;

export const ReferencedInComponentOverride = gql`
  fragment ReferencedInComponentOverride on ReferencedInComponentOverride {
    type
    label
    isHidden
  }
`;

export const UrlComponent = gql`
  fragment UrlComponent on UrlComponent {
    id
    type
    locked
    required
    readonly
    label
    placeholder
    icon
    isHidden
    isFilterable
    isSortable
    isServerManaged
    managedByComponentId
  }
`;

export const UrlComponentOverride = gql`
  fragment UrlComponentOverride on UrlComponentOverride {
    type
    required
    readonly
    label
    placeholder
    icon
    isHidden
  }
`;

export const UrlComponentDataValue = gql`
  fragment UrlComponentDataValue on UrlComponentDataValue {
    url
  }
`;

export const UrlComponentDataChange = gql`
  fragment UrlComponentDataChange on UrlComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    newAuditData {
      type
      urlValue: value {
        ...UrlComponentDataValue
      }
    }
    originalAuditData {
      type
      urlValue: value {
        ...UrlComponentDataValue
      }
    }
  }

  ${UrlComponentDataValue}
`;

export const SubformComponentChildComponentDataChange = gql`
  fragment SubformComponentChildComponentDataChange on AnyComponentDataChange {
    ...AuditCheckboxComponentDataChange
    ...AuditTagComponentDataChange
    ...AuditTextComponentDataChange
    ...AddressComponentDataChange
    ...DateComponentDataChange
    ...EmailComponentDataChange
    ...FileComponentDataChange
    ...GeolocationComponentDataChange
    ...NumberComponentDataChange
    ...PersonComponentDataChange
    ...SignatureComponentDataChange
    ...TagComponentDataChange
    ...TimeTrackerComponentDataChange
    ...TodoComponentDataChange
    ...UrlComponentDataChange
  }

  ${AuditCheckboxComponentDataChange}
  ${AuditTagComponentDataChange}
  ${AuditTextComponentDataChange}
  ${AddressComponentDataChange}
  ${DateComponentDataChange}
  ${EmailComponentDataChange}
  ${FileComponentDataChange}
  ${GeolocationComponentDataChange}
  ${NumberComponentDataChange}
  ${PersonComponentDataChange}
  ${SignatureComponentDataChange}
  ${TagComponentDataChange}
  ${TimeTrackerComponentDataChange}
  ${TodoComponentDataChange}
  ${UrlComponentDataChange}
`;

export const SubformComponentDataChange = gql`
  fragment SubformComponentDataChange on SubformComponentDataChange {
    componentType
    workflowTemplateId
    componentId
    childComponentDataChanges {
      ...SubformComponentChildComponentDataChange
    }
    newAuditData {
      type
      subformValue: value {
        ...SubformComponentAuditDataValue
      }
    }
    originalAuditData {
      type
      subformValue: value {
        ...SubformComponentAuditDataValue
      }
    }
  }

  ${SubformComponentChildComponentDataChange}
  ${SubformComponentAuditDataValue}
`;

// -----------------------------------------------------------------------------
// Component data fragments (used by automation actions)
// -----------------------------------------------------------------------------

export const StaticComponentData = gql`
  fragment StaticComponentData on AnyComponentData {
    ... on AddressComponentData {
      type
      addressComponentDataValue {
        coords {
          lat
          lon
        }
        googlePlaceId
        subtitle
        title
      }
    }
    ... on DateComponentData {
      type
      dateComponentDataValue
    }
    ... on EmailComponentData {
      type
      emailComponentDataValue
    }
    ... on FileComponentData {
      type
      fileComponentDataValue {
        contentType
        height
        name
        url
        width
      }
    }
    ... on GeolocationComponentData {
      type
      geolocationComponentDataValue {
        accuracy
        coords {
          lat
          lon
        }
        date
        userId
      }
    }
    ... on NumberComponentData {
      type
      numberComponentDataValue
    }
    ... on PersonComponentData {
      type
      personComponentDataValue
    }
    ... on RelatedCardComponentData {
      type
      relatedCardComponentDataValue {
        id
        quantity
      }
    }
    ... on SignatureComponentData {
      type
      signatureComponentDataValue {
        contentType
        height
        name
        url
        width
      }
    }
    ... on SubformComponentData {
      type
      subformComponentDataValue {
        data
        workflowTemplateId
      }
    }
    ... on TagComponentData {
      type
      tagComponentDataValue
    }
    ... on TextComponentData {
      type
      textComponentDataValue
    }
    ... on TimeTrackerComponentData {
      type
      timeTrackerComponentDataValue {
        start {
          geolocation {
            accuracy
            coords {
              lat
              lon
            }
            userId
          }
          timestamp
        }
        end {
          geolocation {
            accuracy
            coords {
              lat
              lon
            }
            userId
          }
          timestamp
        }
      }
    }
    ... on TodoComponentData {
      type
      todoComponentDataValue {
        id
        status
        summary
      }
    }
  }
`;

export const DynamicComponentData = gql`
  fragment DynamicComponentData on AnyDynamicComponentData {
    ... on ActionResultNumber {
      type
      actionId
    }
    ... on CurrentWorkflowEntity {
      type
    }
    ... on CurrentWorkflowEntityComponent {
      type
      componentId
      # TODO(scalarKey): re-enable once the backend adds this field (currently causes 400s).
      # scalarKey
    }
    ... on TimeTrackerDataAction {
      type
      dataAction
    }
    ... on RelatedWorkflowEntityComponent {
      type
      relatedCardComponentId
      targetComponentId
      # TODO(scalarKey): re-enable once the backend adds this field (currently causes 400s).
      # scalarKey
    }
    ... on OriginUser {
      type
    }
    ... on ClearValue {
      type
    }
    ... on SubformWorkflowEntityComponent {
      type
      subformComponentId
      refComponentType
      cases {
        componentId
        workflowTemplateId
      }
      # TODO(scalarKey): re-enable once the backend adds this field (currently causes 400s).
      # scalarKey
    }
  }
`;

export const DynamicNumberComponentData = gql`
  fragment DynamicNumberComponentData on AnyDynamicNumberComponentData {
    ... on ActionResultNumber {
      type
      actionId
    }
    ... on CurrentWorkflowEntityComponent {
      type
      componentId
      # TODO(scalarKey): re-enable once the backend adds this field (currently causes 400s).
      # scalarKey
    }
    ... on RelatedWorkflowEntityComponent {
      type
      relatedCardComponentId
      targetComponentId
      # TODO(scalarKey): re-enable once the backend adds this field (currently causes 400s).
      # scalarKey
    }
    ... on SubformWorkflowEntityComponent {
      type
      subformComponentId
      refComponentType
      cases {
        componentId
        workflowTemplateId
      }
      # TODO(scalarKey): re-enable once the backend adds this field (currently causes 400s).
      # scalarKey
    }
  }
`;
